import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 10, fontFamily: 'Helvetica', color: '#334155' },

  section: { marginBottom: 15 },

  row: {
    flexDirection: 'row',
    borderBottom: '0.5 solid #e2e8f0',
    paddingVertical: 5,
    alignItems: 'center',
  },

  tableHeader: {
    backgroundColor: '#f1f5f9',
    fontWeight: 'bold',
    color: '#0f172a',
    borderBottom: '1.5 solid #cbd5e1',
  },

  colMain: { width: '60%', textAlign: 'left', paddingLeft: 5 },
  colLoc: { width: '25%', textAlign: 'left', fontSize: 9 },
  colQty: { width: '15%', textAlign: 'center' },

  // Sub-items (Components)
  subRow: {
    flexDirection: 'row',
    paddingVertical: 3,
    paddingLeft: 20,
    backgroundColor: '#f8fafc',
    fontSize: 9,
    fontStyle: 'italic',
    color: '#64748b',
    borderBottom: '0.5 solid #f1f5f9',
  },

  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginTop: 15,
    marginBottom: 8,
    color: '#1e293b',
    textDecoration: 'underline',
  },

  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 30,
    borderBottom: '2 solid #3b82f6',
    paddingBottom: 10,
  },

  logo: { width: 100, height: 40 },

  headerText: {
    textAlign: 'right',
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1e40af',
  },

  // Firmas
  firmasContainer: {
    marginTop: 40,
    flexDirection: 'row',
    justifyContent: 'space-around',
  },

  firmaBox: {
    width: '38%',
    alignItems: 'center',
  },

  signatureImg: {
    width: 120,
    height: 55,
    marginBottom: 4,
    objectFit: 'contain',
  },

  firmaLinea: {
    marginTop: 5,
    borderTop: '1 solid #475569',
    width: '100%',
    textAlign: 'center',
    paddingTop: 5,
    fontSize: 9,
    fontWeight: 'bold',
  },
});

export default function RemisionPDF({ remision, backendUrl }) {
  const getFullUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('data:') || path.startsWith('blob:')) return path;
    const base = backendUrl || '';
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    return `${base.endsWith('/') ? base : base + '/'}${cleanPath}`;
  };

  return (
    <Document title={`Remisión ${remision.remisionId}`}>
      <Page size="A4" style={styles.page}>
        {/* ENCABEZADO */}
        <View style={styles.headerContainer}>
          <Image style={styles.logo} src={{ uri: '/img/logo.png' }} />
          <View>
            <Text style={styles.headerText}>REMISIÓN DE INVENTARIO</Text>
            <Text style={{ textAlign: 'right', fontSize: 10, color: '#64748b' }}>Remisión #: {String(remision.remisionId)}</Text>
            <Text style={{ textAlign: 'right', fontSize: 10, color: '#64748b' }}>Proyecto #: {remision.projectId != null ? String(remision.projectId) : 'S/N'}</Text>
          </View>
        </View>

        {/* DATOS GENERALES */}
        <View style={styles.section}>
          <Text style={{ fontFamily: 'Helvetica-Bold', marginBottom: 3 }}>
            {'Fecha: '}<Text style={{ fontFamily: 'Helvetica' }}>{remision.fecha || 'S/N'}</Text>
          </Text>
          <Text style={{ fontFamily: 'Helvetica-Bold', marginBottom: 3 }}>
            {'Proyecto #: '}<Text style={{ fontFamily: 'Helvetica' }}>{remision.projectId !== undefined && remision.projectId !== null && remision.projectId !== '' ? String(remision.projectId) : 'S/N'}</Text>
          </Text>
          <Text style={{ fontFamily: 'Helvetica-Bold', marginBottom: 3 }}>
            {'Cliente: '}<Text style={{ fontFamily: 'Helvetica' }}>{remision.cliente || 'S/N'}</Text>
          </Text>
          <Text style={{ fontFamily: 'Helvetica-Bold', marginBottom: 3 }}>
            {'Descripción: '}<Text style={{ fontFamily: 'Helvetica' }}>{remision.description || 'S/N'}</Text>
          </Text>
        </View>

        {/* SECCIÓN DE PRODUCTOS */}
        {remision.products && remision.products.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Productos en Remisión</Text>
            <View style={[styles.row, styles.tableHeader]}>
              <Text style={styles.colMain}>Producto / Componente</Text>
              <Text style={styles.colLoc}>Ubicación</Text>
              <Text style={styles.colQty}>Cantidad</Text>
            </View>

            {remision.products.map((prod, pIdx) => (
              <View key={`prod-${pIdx}`}>
                {/* COMPONENTES DEL PRODUCTO */}
                {(prod.components || []).map((comp, cIdx) => (
                  <View key={`comp-${pIdx}-${cIdx}`} style={styles.subRow}>
                    <Text style={styles.colMain}>• #{comp.item_id || comp.id} - {comp.name}</Text>
                    <Text style={styles.colLoc}>{comp.ubicacion}</Text>
                    <Text style={styles.colQty}>{comp.totalQuantity}</Text>
                  </View>
                ))}
                <View style={styles.row}>
                  <Text style={[styles.colMain, { fontWeight: 'bold' }]}>#{prod.product_id || prod.id} - {prod.name}</Text>
                  <Text style={styles.colLoc}></Text>
                  <Text style={[styles.colQty, { fontWeight: 'bold' }]}>{prod.cantidad}</Text>
                </View>
              </View>
            ))}
          </View>
        )}

        {/* SECCIÓN DE ITEMS INDIVIDUALES */}
        {remision.items && remision.items.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Items Adicionales</Text>
            <View style={[styles.row, styles.tableHeader]}>
              <Text style={styles.colMain}>Item</Text>
              <Text style={styles.colLoc}>Ubicación</Text>
              <Text style={styles.colQty}>Cantidad</Text>
            </View>

            {remision.items.map((item, index) => (
              <View key={`item-${index}`} style={styles.row}>
                <Text style={styles.colMain}>#{item.item_id || item.id} - {item.description}</Text>
                <Text style={styles.colLoc}>{item.ubicacion}</Text>
                <Text style={styles.colQty}>{item.cantidad}</Text>
              </View>
            ))}
          </View>
        )}

        {/* FIRMAS */}
        <View style={styles.firmasContainer} wrap={false}>
          <View style={styles.firmaBox}>
            <View style={{ height: 60, justifyContent: 'flex-end', alignItems: 'center' }}>
              <Text style={{ marginBottom: 4, fontSize: 9 }}>{remision.elaboradoPor || ' '}</Text>
            </View>
            <Text style={styles.firmaLinea}>Elaborado por (DIMEI)</Text>
          </View>

          <View style={styles.firmaBox}>
            <View style={{ height: 60, justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
              {remision.customer_signature ? (
                <Image style={styles.signatureImg} src={getFullUrl(remision.customer_signature)} />
              ) : null}
            </View>
            <Text style={styles.firmaLinea}>Recibido conforme (Cliente)</Text>
            {remision.customer_name ? (
              <Text style={{ fontSize: 8, color: '#475569', marginTop: 2 }}>{remision.customer_name}</Text>
            ) : (
              <Text style={{ fontSize: 8, color: '#475569', marginTop: 2 }}>{remision.aprobadoPor || ' '}</Text>
            )}
            {remision.signed_date ? (
              <Text style={{ fontSize: 7, color: '#94a3b8', marginTop: 1 }}>
                Firma: {new Date(remision.signed_date).toLocaleDateString()}
              </Text>
            ) : null}
          </View>
        </View>
      </Page>
    </Document>
  );
}
